/**
 * In-process scheduler — timers that fire prompts into the agent loop while the session is alive.
 * Three trigger modes: one-off ({at}), interval ({everyMs}), cron ({cron}).
 * The scheduler never calls `turn()` itself — it invokes an IoC `fire` callback with due jobs,
 * keeping it testable and host-agnostic. Persistence is the host's responsibility (serialize jobs
 * into the session and re-arm on resume).
 *
 * Survives --resume (host reloads); does NOT survive process exit (no daemon, no launchd).
 */

export type TriggerOneOff = { at: number };
export type TriggerInterval = { everyMs: number };
export type TriggerCron = { cron: string };
export type Trigger = TriggerOneOff | TriggerInterval | TriggerCron;

export type ScheduledJobStatus = 'active' | 'done';

export interface ScheduledJob {
  id: string;
  prompt: string;
  trigger: Trigger;
  status: ScheduledJobStatus;
  created: number;
  lastRun?: number;
  runs: number;
  /** Optional label for display (e.g. footer, /schedule list). */
  label?: string;
}

/** Serializable snapshot for session persistence (same shape — kept as an alias for clarity at call sites). */
export type ScheduledJobSnapshot = ScheduledJob;

export interface SchedulerOptions {
  /** Called when a job is due. Must not throw — the scheduler logs errors and continues. */
  fire: (job: ScheduledJob) => Promise<void> | void;
  /** Monotonic clock override for testing (default: Date.now). */
  now?: () => number;
  /** Tick interval in ms (default: 15_000 — 15s). */
  tickMs?: number;
  /** If true, suppress the setInterval (for unit tests that call tick() manually). */
  manual?: boolean;
}

// ── Cron mini-parser (minute hour dom month dow) ──────────────────────────
// Supports: *, N, N-M, */N, N-M/N, comma-separated lists. No named days/months.
// Enough for real scheduling; not a full vixie-cron (no @yearly etc).

interface CronFields { minute: number[]; hour: number[]; dom: number[]; month: number[]; dow: number[] }

function parseCronField(field: string, min: number, max: number): number[] {
  const vals = new Set<number>();
  for (const part of field.split(',')) {
    const [rangeStr, stepStr] = part.split('/');
    const step = stepStr ? parseInt(stepStr, 10) : 1;
    if (isNaN(step) || step < 1) throw new Error(`invalid cron step: ${part}`);
    let lo: number, hi: number;
    if (rangeStr === '*') { lo = min; hi = max; }
    else if (rangeStr!.includes('-')) {
      const [a, b] = rangeStr!.split('-').map(Number);
      if (isNaN(a!) || isNaN(b!)) throw new Error(`invalid cron range: ${part}`);
      lo = a!; hi = b!;
    } else {
      const n = parseInt(rangeStr!, 10);
      if (isNaN(n)) throw new Error(`invalid cron value: ${part}`);
      lo = n; hi = stepStr ? max : n;
    }
    for (let i = lo; i <= hi; i += step) vals.add(i);
  }
  return [...vals].sort((a, b) => a - b);
}

export function parseCron(expr: string): CronFields {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) throw new Error(`cron expression must have 5 fields (minute hour dom month dow), got ${parts.length}: "${expr}"`);
  return {
    minute: parseCronField(parts[0]!, 0, 59),
    hour: parseCronField(parts[1]!, 0, 23),
    dom: parseCronField(parts[2]!, 1, 31),
    month: parseCronField(parts[3]!, 1, 12),
    dow: parseCronField(parts[4]!, 0, 6),
  };
}

export function cronMatches(fields: CronFields, date: Date): boolean {
  return fields.minute.includes(date.getMinutes())
    && fields.hour.includes(date.getHours())
    && fields.dom.includes(date.getDate())
    && fields.month.includes(date.getMonth() + 1)
    && fields.dow.includes(date.getDay());
}

/** Next occurrence of `cron` after `after` (epoch ms). Returns epoch ms, or null if >366 days out (guard). */
export function nextCronAfter(cron: string, after: number): number | null {
  const fields = parseCron(cron);
  const d = new Date(after);
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 1); // always at least 1 minute in the future
  const limit = after + 366 * 86400_000;
  while (d.getTime() <= limit) {
    if (cronMatches(fields, d)) return d.getTime();
    d.setMinutes(d.getMinutes() + 1);
  }
  return null;
}

// ── Scheduler ─────────────────────────────────────────────────────────────

export class Scheduler {
  private jobs = new Map<string, ScheduledJob>();
  private seq = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private firing = false;
  private readonly fire: SchedulerOptions['fire'];
  private readonly now: () => number;
  private readonly tickMs: number;

  constructor(opts: SchedulerOptions) {
    this.fire = opts.fire;
    this.now = opts.now ?? Date.now;
    this.tickMs = opts.tickMs ?? 15_000;
    if (!opts.manual) this.start();
  }

  /** Start the tick timer. Idempotent. */
  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), this.tickMs);
    if (typeof this.timer === 'object' && 'unref' in this.timer) (this.timer as any).unref();
  }

  /** Stop the tick timer. Does not clear jobs. */
  stop(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  /** Add a scheduled job. Returns the job id. */
  add(opts: { prompt: string; trigger: Trigger; label?: string }): string {
    // Validate trigger
    if ('cron' in opts.trigger) parseCron(opts.trigger.cron); // throws on bad expr
    if ('at' in opts.trigger && opts.trigger.at < this.now()) {
      // Allow slightly-past timestamps (clock skew); just fire on next tick.
    }
    if ('everyMs' in opts.trigger && opts.trigger.everyMs < 1000) {
      throw new Error('interval must be >= 1000ms');
    }
    const id = `sched-${++this.seq}`;
    this.jobs.set(id, {
      id, prompt: opts.prompt, trigger: opts.trigger, status: 'active',
      created: this.now(), runs: 0, label: opts.label,
    });
    return id;
  }

  /** Cancel a job. Returns false if not found. */
  cancel(id: string): boolean {
    const j = this.jobs.get(id);
    if (!j) return false;
    j.status = 'done';
    return true;
  }

  get(id: string): ScheduledJob | undefined { return this.jobs.get(id); }

  list(): ScheduledJob[] { return [...this.jobs.values()]; }

  active(): ScheduledJob[] { return this.list().filter(j => j.status === 'active'); }

  /** Compute when a job next fires (epoch ms), or null if done/unknown. */
  nextFire(job: ScheduledJob): number | null {
    if (job.status !== 'active') return null;
    const t = job.trigger;
    if ('at' in t) return t.at;
    if ('everyMs' in t) return (job.lastRun ?? job.created) + t.everyMs;
    if ('cron' in t) return nextCronAfter(t.cron, job.lastRun ?? job.created);
    return null;
  }

  /** Check all active jobs and fire any that are due. Reentrant-safe. */
  async tick(): Promise<void> {
    if (this.firing) return; // guard: never overlap
    this.firing = true;
    try {
      const now = this.now();
      for (const job of this.jobs.values()) {
        if (job.status !== 'active') continue;
        const due = this.nextFire(job);
        if (due == null || due > now) continue;
        job.lastRun = now;
        job.runs++;
        // One-off: mark done immediately (before firing — fire is async, cancel must stick).
        if ('at' in job.trigger) job.status = 'done';
        try { await this.fire(job); } catch { /* fire contract: must not throw; belt-and-suspenders */ }
      }
    } finally {
      this.firing = false;
    }
  }

  /** Export all jobs for session persistence. */
  snapshot(): ScheduledJobSnapshot[] {
    return this.list().filter(j => j.status === 'active').map(j => ({ ...j }));
  }

  /** Restore jobs from a snapshot (e.g. on --resume). Clears existing jobs first. */
  restore(snapshots: ScheduledJobSnapshot[]): void {
    this.jobs.clear();
    for (const s of snapshots) {
      this.seq = Math.max(this.seq, parseInt(s.id.replace('sched-', ''), 10) || 0);
      this.jobs.set(s.id, { ...s });
    }
  }

  /** Number of active jobs. */
  get size(): number { return this.active().length; }

  destroy(): void { this.stop(); this.jobs.clear(); }
}

// ── Agent tools ───────────────────────────────────────────────────────────

import type { AgentTool } from './tools';

/** Narrow seam to an OS-level scheduler backend (jobs that survive quit/reboot). Node hosts wire
 *  the CLI's `OsScheduler` (launchd/cron/at); absent => everything stays in-process. Edge-safe:
 *  this module only sees the interface. */
export interface OsBackend {
  sessionId: string;
  cwd: string;
  /** 'os' when the trigger should outlive the session (per hint/heuristic). */
  route(trigger: Trigger, backendHint?: string): 'session' | 'os';
  /** Register with the OS. Returns a mechanism description (e.g. 'launchd:…'). Throws on failure. */
  schedule(spec: { id: string; prompt: string; sessionId: string; cwd: string; trigger: Trigger; label?: string }): string;
  cancel(id: string): boolean;
  list(): Array<{ id: string; label?: string; mechanism: string; trigger: Trigger }>;
}

export function makeScheduleTools(scheduler: Scheduler, os?: OsBackend): AgentTool[] {
  return [
    {
      name: 'ScheduleTask',
      description:
        'Schedule a prompt to fire automatically.\n' +
        'Modes:\n' +
        '  • One-off:   {at: <epoch_ms>}        — fires once at that time, then done.\n' +
        '  • Interval:  {everyMs: <ms>}          — fires repeatedly (≥1s).\n' +
        '  • Cron:      {cron: "min hr dom mon dow"} — standard 5-field cron.\n' +
        'Backend: "session" fires while this CLI session is alive (default for recurring + near one-offs); ' +
        '"os" registers with the OS scheduler so the job survives quitting — it headless-resumes this session ' +
        'when it fires (auto-chosen for one-offs ≥30min out when available). Pass backend:"os" explicitly for ' +
        'recurring jobs that must outlive the session.',
      parameters: {
        type: 'object',
        required: ['prompt', 'trigger'],
        properties: {
          prompt: { type: 'string', description: 'The prompt to inject when the job fires.' },
          trigger: {
            type: 'object',
            description: 'One of: {at: epoch_ms}, {everyMs: ms}, {cron: "5-field expr"}.',
            properties: {
              at: { type: 'number' },
              everyMs: { type: 'number' },
              cron: { type: 'string' },
            },
          },
          label: { type: 'string', description: 'Short label for display (optional).' },
          backend: { type: 'string', enum: ['auto', 'session', 'os'], description: 'Where the job lives (default auto).' },
        },
      },
      async run({ prompt, trigger, label, backend }) {
        try {
          if (os && os.route(trigger, backend) === 'os') {
            const id = `os-${Date.now().toString(36)}`;
            const mechanism = os.schedule({ id, prompt, sessionId: os.sessionId, cwd: os.cwd, trigger, label });
            return `Scheduled ${id}${label ? ` (${label})` : ''} on the OS scheduler (${mechanism}) — survives quitting; fires \`agentx --resume ${os.sessionId}\` headless.`;
          }
          if (backend === 'os') return 'Error: no OS scheduler available on this platform — job not created (use the default in-session backend).';
          const id = scheduler.add({ prompt, trigger, label });
          const job = scheduler.get(id)!;
          const next = scheduler.nextFire(job);
          return `Scheduled ${id}${label ? ` (${label})` : ''}. Next fire: ${next ? new Date(next).toLocaleString() : 'now'}. (In-session: does not survive quitting.)`;
        } catch (e: any) {
          return `Error: ${e?.message ?? e}`;
        }
      },
    },
    {
      name: 'ScheduleList',
      description: 'List all scheduled jobs (in-session + OS-backed) and their next fire time.',
      parameters: { type: 'object', properties: {} },
      async run() {
        const osJobs = os?.list() ?? [];
        const osLines = osJobs.map((j) => `${j.id}  os  ${j.mechanism}${j.label ? '  ' + j.label : ''}`);
        const jobs = scheduler.list();
        if (!jobs.length && !osLines.length) return '(no scheduled jobs)';
        return [...osLines, ...jobs.map(j => {
          const next = scheduler.nextFire(j);
          const trig = 'at' in j.trigger ? `once @ ${new Date(j.trigger.at).toLocaleString()}`
            : 'everyMs' in j.trigger ? `every ${(j.trigger.everyMs / 1000).toFixed(0)}s`
            : `cron: ${(j.trigger as TriggerCron).cron}`;
          return `${j.id}  ${j.status}  ${trig}  runs:${j.runs}  next:${next ? new Date(next).toLocaleTimeString() : '—'}${j.label ? '  ' + j.label : ''}`;
        })].join('\n');
      },
    },
    {
      name: 'ScheduleCancel',
      description: 'Cancel a scheduled job by id (in-session or OS-backed).',
      parameters: { type: 'object', required: ['id'], properties: { id: { type: 'string' } } },
      async run({ id }) {
        const key = String(id);
        if (key.startsWith('os-')) return os?.cancel(key) ? `Cancelled ${key} (OS job removed).` : `Error: no OS job '${key}'.`;
        return scheduler.cancel(key) ? `Cancelled ${key}.` : `Error: no scheduled job '${key}'. Use ScheduleList to see jobs.`;
      },
    },
    {
      name: 'Wakeup',
      description:
        'Self-pacing: schedule a one-off re-invocation of the agent after a delay.\n' +
        'Use this to resume work later in the session (e.g. "check back in 5 minutes").\n' +
        'The prompt fires once, while the session is alive. Equivalent to ScheduleTask with {at: now + delayMs}.',
      parameters: {
        type: 'object',
        required: ['delayMs', 'prompt'],
        properties: {
          delayMs: { type: 'number', description: 'Delay in milliseconds (minimum 5000).' },
          prompt: { type: 'string', description: 'The prompt to inject when waking up.' },
          label: { type: 'string' },
        },
      },
      async run({ delayMs, prompt, label }) {
        const delay = Math.max(5000, Number(delayMs) || 5000);
        try {
          const id = scheduler.add({ prompt, trigger: { at: Date.now() + delay }, label: label ?? 'wakeup' });
          return `Wakeup ${id} in ${(delay / 1000).toFixed(0)}s.`;
        } catch (e: any) {
          return `Error: ${e?.message ?? e}`;
        }
      },
    },
  ];
}
